import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectUserInfo, logout } from "@redux/slices/authSlice";
import { Menu, X } from "lucide-react";
import logo from "@assets/images/logo2.png"
import config from "../../config";

// Helper function to check if user can access members page
const canAccessMembers = (userInfo) => {
    if (!userInfo || !userInfo.role) return false;
    return userInfo.role !== "user";
};

// Helper function to validate user info
const validateUserInfo = (userInfo) => {
    if (!userInfo) return false;
    if (!userInfo.role || !userInfo.email || !userInfo.first_name) return false;
    return true;
};

const Navbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userInfo = useSelector(selectUserInfo);
    const location = useLocation();
    const isLoginPage = location.pathname === '/login';
    const isResetPage = location.pathname === '/force-password-reset';



    const menuItems = [
        {
            name: "Home",
            path: "/",
        },
        {
            name: "Start Monitoring",
            path: "/integrations/listing",
        },
    ];

    const handleGetStarted = () => {
        setMenuOpen(false);
        navigate("/login");
    };

    const handleLogout = () => {
        dispatch(logout());
        setMenuOpen(false);
        navigate("/login");
    };

    return (
        <header className="sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md text-gray-900 shadow-sm border-b flex-none">
            <div className="flex items-center justify-between px-6 sm:px-12 py-3">
                {/* Logo */}
                {isLoginPage ? (
                    <a href={config.LANDING_URL} className="cursor-pointer" target="_blank" rel="noopener noreferrer">
                        <div className="text-gray-900 font-semibold flex flex-col items-start leading-tight hover:opacity-80 transition-opacity">
                            <h4 className="flex items-center gap-2 text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent tracking-wider">
                                <img className="w-9 h-9" src={logo} alt="Logo" />Zyntegrate</h4>
                            <p className="text-[11px] font-semibold text-[#475569] ml-11 -mt-0.5 tracking-wide">Powered by Z-Ninth</p>
                        </div>
                    </a>
                ) : (
                    <Link
                        to="/"
                        className="text-gray-900 font-semibold flex flex-col items-start leading-tight hover:opacity-80 transition-opacity"
                    >
                        <h4 className="flex items-center gap-2 text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent tracking-wider">
                            <img className="w-9 h-9" src={logo} alt="Logo" />Zyntegrate</h4>
                        <p className="text-[11px] font-semibold text-[#475569] ml-11 -mt-0.5 tracking-wide">Powered by Z-Ninth</p>
                    </Link>
                )}

                <div className="hidden md:flex items-center gap-6 text-sm">
                    {userInfo && !isResetPage && (
                        <>
                            <Link
                                to="/"
                                className={`transition-all duration-200 hover:text-blue-600 px-1 py-1 ${location.pathname === "/" ? "text-blue-600 font-bold border-b-2 border-blue-600" : "text-[#1e293b]"}`}
                            >
                                Home
                            </Link>

                            {canAccessMembers(userInfo) && (
                                <>
                                    <Link
                                        to={'/memberslisting'}
                                        className={`transition-all duration-200 hover:text-blue-600 px-1 py-1 ${location.pathname === "/memberslisting" ? "text-blue-600 font-bold border-b-2 border-blue-600" : "text-[#1e293b]"}`}
                                    >
                                        Members
                                    </Link>
                                    <Link
                                        to="/connections"
                                        className={`transition-all duration-200 hover:text-blue-600 px-1 py-1 ${location.pathname === "/connections" ? "text-blue-600 font-bold border-b-2 border-blue-600" : "text-[#1e293b]"}`}
                                    >
                                        Connections
                                    </Link>
                                </>
                            )}

                            <Link
                                to="/settings"
                                className={`transition-all duration-200 hover:text-blue-600 px-1 py-1 ${location.pathname === "/settings" ? "text-blue-600 font-bold border-b-2 border-blue-600" : "text-[#1e293b]"}`}
                            >
                                Settings
                            </Link>

                            {userInfo?.role?.toLowerCase() === "superadmin" && (
                                <Link
                                    to={'/companies'}
                                    className={`transition-all duration-200 hover:text-blue-600 px-1 py-1 ${location.pathname === "/companies" ? "text-blue-600 font-bold border-b-2 border-blue-600" : "text-[#1e293b]"}`}
                                >
                                    Control Panel
                                </Link>
                            )}
                        </>
                    )}
                </div>

                <div className="hidden md:flex items-center gap-4 text-sm">
                    {userInfo && !isResetPage ? (
                        <div className="flex items-center gap-6">
                            <span className="text-sm font-medium text-[#1e293b]">
                                Welcome, <span className="font-bold text-blue-600 tracking-tight">{userInfo.first_name}</span>
                            </span>
                            {!validateUserInfo(userInfo) && (
                                <span className="text-yellow-400 text-xs bg-yellow-900 px-2 py-1 rounded">
                                    Invalid user info - please logout
                                </span>
                            )}
                            <button
                                onClick={handleLogout}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2 rounded-full transition-all duration-200 text-sm active:scale-95 shadow-lg hover:shadow-blue-200"
                            >
                                Logout
                            </button>
                        </div>
                    ) : (
                        <>
                            {!isLoginPage && !isResetPage && (
                                <button
                                    onClick={handleGetStarted}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-1.5 rounded-md transition text-xs active:scale-95 shadow-sm"
                                >
                                    Get Started
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* Hamburger Icon */}
                <div className="md:hidden">
                    <button onClick={() => setMenuOpen(!menuOpen)} className="focus:outline-none">
                        {menuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Dropdown Menu */}
            {menuOpen && (
                <div className="md:hidden bg-white border-t px-6 py-4 space-y-4 text-sm shadow-lg">
                    {userInfo && !isResetPage && (
                        <div className="flex flex-col gap-4">
                            <Link
                                to="/"
                                onClick={() => setMenuOpen(false)}
                                className={`block py-1 hover:text-blue-600 ${location.pathname === "/" ? "text-blue-600 font-bold" : "text-gray-900"}`}
                            >
                                Home
                            </Link>

                            {canAccessMembers(userInfo) && (
                                <>
                                    <Link
                                        to="/memberslisting"
                                        onClick={() => setMenuOpen(false)}
                                        className={`block py-1 hover:text-blue-600 ${location.pathname === "/memberslisting" ? "text-blue-600 font-bold" : "text-gray-900"}`}
                                    >
                                        Members
                                    </Link>
                                    <Link
                                        to="/connections"
                                        onClick={() => setMenuOpen(false)}
                                        className={`block py-1 hover:text-blue-600 ${location.pathname === "/connections" ? "text-blue-600 font-bold" : "text-gray-900"}`}
                                    >
                                        Connections
                                    </Link>
                                </>
                            )}

                            <Link
                                to="/settings"
                                onClick={() => setMenuOpen(false)}
                                className={`block py-1 hover:text-blue-600 ${location.pathname === "/settings" ? "text-blue-600 font-bold" : "text-gray-900"}`}
                            >
                                Settings
                            </Link>

                            {userInfo?.role?.toLowerCase() === "superadmin" && (
                                <Link
                                    to="/companies"
                                    onClick={() => setMenuOpen(false)}
                                    className={`block py-1 hover:text-blue-600 ${location.pathname === "/companies" ? "text-blue-600 font-bold" : "text-gray-900"}`}
                                >
                                    Control Panel
                                </Link>
                            )}
                        </div>
                    )}

                    {!userInfo && menuItems.map((item) => (
                        <Link
                            key={item.name}
                            to={item.path}
                            onClick={() => setMenuOpen(false)}
                            className={`block hover:text-blue-600 flex items-center gap-2 ${location.pathname === item.path ? "text-blue-600 font-bold" : "text-gray-900"}`}
                        >
                            {item.icon}
                            {item.name}
                        </Link>
                    ))}

                    <div className="border-t border-gray-100 pt-4 text-gray-900">
                        {userInfo && !isResetPage ? (
                            <div className="space-y-4">
                                <div className="text-sm font-medium text-[#1e293b]">
                                    Welcome, <span className="font-bold text-blue-600 uppercase tracking-tight">{userInfo.first_name}</span>
                                </div>

                                <button
                                    onClick={handleLogout}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2.5 rounded-full transition-all duration-200 text-sm active:scale-95 shadow-lg"
                                >
                                    Logout
                                </button>
                            </div>
                        ) : (
                            <>
                                {!isLoginPage && (
                                    <button
                                        onClick={handleGetStarted}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md transition"
                                    >
                                        Get Started
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;